import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { Wifi, User, Phone, Mail, Loader2, CheckCircle2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WiFiPortalPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [config, setConfig] = useState(null);
  const [activeTab, setActiveTab] = useState("login");
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [userName, setUserName] = useState("");

  // Captive portal params from TP-Link
  const clientMac = searchParams.get("clientMac") || searchParams.get("client_mac") || "";
  const clientIp = searchParams.get("clientIP") || searchParams.get("client_ip") || "";
  const apMac = searchParams.get("apMac") || searchParams.get("ap_mac") || "";
  const ssidName = searchParams.get("ssidName") || searchParams.get("ssid") || "";
  const originalUrl = searchParams.get("originalUrl") || searchParams.get("redirect") || "";
  const filialId = searchParams.get("filial_id") || searchParams.get("filial") || "";

  // Login form
  const [loginForm, setLoginForm] = useState({
    telefone: "",
    email: ""
  });

  // Register form
  const [registerForm, setRegisterForm] = useState({
    nome_completo: "",
    telefone: "",
    email: "",
    data_nascimento: "",
    sexo: "",
    cidade: "",
    endereco: ""
  });

  useEffect(() => {
    loadConfig();
  }, [filialId]);

  const loadConfig = async () => {
    try {
      // Get filial_id from URL or try to detect from SSID
      let targetFilialId = filialId;
      
      if (!targetFilialId) {
        // Try to get from branch list (use first active)
        try {
          const branchesRes = await axios.get(`${API}/branches`);
          if (branchesRes.data && branchesRes.data.length > 0) {
            targetFilialId = branchesRes.data[0].id;
          }
        } catch (e) {
          console.error("Error fetching branches:", e);
        }
      }

      if (targetFilialId) {
        const response = await axios.get(`${API}/wifi/portal/config/${targetFilialId}`);
        setConfig(response.data);
      } else {
        // Use default config
        setConfig({
          titulo_portal: "Bem-vindo ao WiFi",
          subtitulo_portal: "Faça login ou cadastre-se para acessar a internet",
          campos_obrigatorios: ["nome_completo", "telefone"],
          campos_visiveis: ["nome_completo", "telefone", "email"],
          cor_primaria: "#2563eb"
        });
      }
    } catch (error) {
      console.error("Error loading config:", error);
      // Use default config on error
      setConfig({
        titulo_portal: "Bem-vindo ao WiFi",
        subtitulo_portal: "Faça login ou cadastre-se para acessar a internet",
        campos_obrigatorios: ["nome_completo", "telefone"],
        campos_visiveis: ["nome_completo", "telefone", "email"],
        cor_primaria: "#2563eb"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginForm.telefone && !loginForm.email) {
      toast.error("Informe seu telefone ou email");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/wifi/portal/login`, {
        telefone: loginForm.telefone,
        email: loginForm.email || null,
        client_mac: clientMac,
        client_ip: clientIp,
        ap_mac: apMac,
        ssid_name: ssidName,
        original_url: originalUrl,
        filial_id: config?.filial_id || filialId
      });

      if (response.data.success) {
        setSuccess(true);
        setUserName(response.data.pessoa?.nome || "Visitante");
        setRedirectUrl(response.data.redirect_url);
        toast.success(response.data.message);

        // Redirect after 3 seconds if URL provided
        if (response.data.redirect_url) {
          setTimeout(() => {
            window.location.href = response.data.redirect_url;
          }, 3000);
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao fazer login";
      toast.error(message);
      
      // If not found, suggest registration
      if (error.response?.status === 404) {
        setActiveTab("register");
        if (loginForm.telefone) {
          setRegisterForm(prev => ({ ...prev, telefone: loginForm.telefone }));
        }
        if (loginForm.email) {
          setRegisterForm(prev => ({ ...prev, email: loginForm.email }));
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    // Validate required fields
    const required = config?.campos_obrigatorios || ["nome_completo", "telefone"];
    for (const field of required) {
      if (!registerForm[field]) {
        toast.error(`Campo obrigatório: ${getFieldLabel(field)}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/wifi/portal/register`, {
        ...registerForm,
        client_mac: clientMac,
        client_ip: clientIp,
        ap_mac: apMac,
        ssid_name: ssidName,
        original_url: originalUrl,
        filial_id: config?.filial_id || filialId
      });

      if (response.data.success) {
        setSuccess(true);
        setUserName(response.data.pessoa?.nome || registerForm.nome_completo);
        setRedirectUrl(response.data.redirect_url);
        toast.success(response.data.message);

        // Redirect after 3 seconds if URL provided
        if (response.data.redirect_url) {
          setTimeout(() => {
            window.location.href = response.data.redirect_url;
          }, 3000);
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao fazer cadastro";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const getFieldLabel = (field) => {
    const labels = {
      nome_completo: "Nome Completo",
      telefone: "Telefone",
      email: "Email",
      data_nascimento: "Data de Nascimento",
      sexo: "Sexo",
      cidade: "Cidade",
      endereco: "Endereço"
    };
    return labels[field] || field;
  };

  const isFieldVisible = (field) => {
    return config?.campos_visiveis?.includes(field) ?? true;
  };

  const isFieldRequired = (field) => {
    return config?.campos_obrigatorios?.includes(field) ?? false;
  };

  const primaryColor = config?.cor_primaria || "#2563eb";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-100 p-4">
        <Card className="w-full max-w-md shadow-2xl">
          <CardContent className="pt-8 pb-8 text-center">
            <div 
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <CheckCircle2 className="h-12 w-12" style={{ color: primaryColor }} />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Conectado!
            </h2>
            
            <p className="text-gray-600 mb-6">
              Bem-vindo, <strong>{userName}</strong>!<br />
              Você está conectado ao WiFi.
            </p>

            {redirectUrl ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-500">
                  Redirecionando em 3 segundos...
                </p>
                <Button 
                  onClick={() => window.location.href = redirectUrl}
                  style={{ backgroundColor: primaryColor }}
                  className="w-full"
                >
                  Continuar Navegando
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Você já pode navegar na internet!
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 flex items-center justify-center">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="text-center pb-2">
          {config?.logo_url ? (
            <img 
              src={config.logo_url} 
              alt="Logo" 
              className="h-16 object-contain mx-auto mb-4"
            />
          ) : (
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <Wifi className="h-8 w-8" style={{ color: primaryColor }} />
            </div>
          )}
          
          <CardTitle className="text-2xl">
            {config?.titulo_portal || "Bem-vindo ao WiFi"}
          </CardTitle>
          <CardDescription className="mt-2">
            {config?.subtitulo_portal || "Faça login ou cadastre-se para acessar a internet"}
          </CardDescription>
          
          {config?.filial_nome && (
            <p className="text-sm text-gray-500 mt-1">{config.filial_nome}</p>
          )}
        </CardHeader>

        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login">Já tenho cadastro</TabsTrigger>
              <TabsTrigger value="register">Novo cadastro</TabsTrigger>
            </TabsList>

            {/* Login Tab */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-telefone">
                    <Phone className="h-4 w-4 inline mr-2" />
                    Telefone
                  </Label>
                  <Input
                    id="login-telefone"
                    type="tel"
                    placeholder="(62) 99999-9999"
                    value={loginForm.telefone}
                    onChange={(e) => setLoginForm({ ...loginForm, telefone: e.target.value })}
                    data-testid="wifi-login-phone"
                  />
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-muted-foreground">ou</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-email">
                    <Mail className="h-4 w-4 inline mr-2" />
                    Email
                  </Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="seu@email.com"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    data-testid="wifi-login-email"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={submitting}
                  style={{ backgroundColor: primaryColor }}
                  data-testid="wifi-login-submit"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Wifi className="h-4 w-4 mr-2" />
                  )}
                  Conectar ao WiFi
                </Button>
              </form>
            </TabsContent>

            {/* Register Tab */}
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                {/* Nome Completo */}
                {isFieldVisible("nome_completo") && (
                  <div className="space-y-2">
                    <Label htmlFor="nome_completo">
                      <User className="h-4 w-4 inline mr-2" />
                      Nome Completo {isFieldRequired("nome_completo") && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      id="nome_completo"
                      placeholder="Seu nome completo"
                      value={registerForm.nome_completo}
                      onChange={(e) => setRegisterForm({ ...registerForm, nome_completo: e.target.value })}
                      required={isFieldRequired("nome_completo")}
                      data-testid="wifi-register-name"
                    />
                  </div>
                )}

                {/* Telefone */}
                {isFieldVisible("telefone") && (
                  <div className="space-y-2">
                    <Label htmlFor="telefone">
                      <Phone className="h-4 w-4 inline mr-2" />
                      Telefone {isFieldRequired("telefone") && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      id="telefone"
                      type="tel"
                      placeholder="(62) 99999-9999"
                      value={registerForm.telefone}
                      onChange={(e) => setRegisterForm({ ...registerForm, telefone: e.target.value })}
                      required={isFieldRequired("telefone")}
                      data-testid="wifi-register-phone"
                    />
                  </div>
                )}

                {/* Email */}
                {isFieldVisible("email") && (
                  <div className="space-y-2">
                    <Label htmlFor="email">
                      <Mail className="h-4 w-4 inline mr-2" />
                      Email {isFieldRequired("email") && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      required={isFieldRequired("email")}
                      data-testid="wifi-register-email"
                    />
                  </div>
                )}

                {/* Data de Nascimento */}
                {isFieldVisible("data_nascimento") && (
                  <div className="space-y-2">
                    <Label htmlFor="data_nascimento">
                      Data de Nascimento {isFieldRequired("data_nascimento") && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      id="data_nascimento"
                      type="date"
                      value={registerForm.data_nascimento}
                      onChange={(e) => setRegisterForm({ ...registerForm, data_nascimento: e.target.value })}
                      required={isFieldRequired("data_nascimento")}
                    />
                  </div>
                )}

                {/* Sexo */}
                {isFieldVisible("sexo") && (
                  <div className="space-y-2">
                    <Label htmlFor="sexo">
                      Sexo {isFieldRequired("sexo") && <span className="text-red-500">*</span>}
                    </Label>
                    <Select
                      value={registerForm.sexo}
                      onValueChange={(value) => setRegisterForm({ ...registerForm, sexo: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Masculino">Masculino</SelectItem>
                        <SelectItem value="Feminino">Feminino</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Cidade */}
                {isFieldVisible("cidade") && (
                  <div className="space-y-2">
                    <Label htmlFor="cidade">
                      Cidade {isFieldRequired("cidade") && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      id="cidade"
                      placeholder="Sua cidade"
                      value={registerForm.cidade}
                      onChange={(e) => setRegisterForm({ ...registerForm, cidade: e.target.value })}
                      required={isFieldRequired("cidade")}
                    />
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={submitting}
                  style={{ backgroundColor: primaryColor }}
                  data-testid="wifi-register-submit"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Wifi className="h-4 w-4 mr-2" />
                  )}
                  Cadastrar e Conectar
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          {/* Debug info - hidden in production */}
          {process.env.NODE_ENV === "development" && clientMac && (
            <div className="mt-4 p-2 bg-gray-100 rounded text-xs text-gray-500">
              <p>MAC: {clientMac}</p>
              <p>IP: {clientIp}</p>
              <p>SSID: {ssidName}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
